import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Smartphone, ShoppingBag, CreditCard, CheckCircle2, ChevronRight, User, Plus, Minus, ArrowLeft, Utensils, Sparkles } from 'lucide-react';
import { supabase } from '../supabase/config';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import DigitalMenu from './DigitalMenu';

import { useCustomers } from '../context/CustomerContext';
import { orderService } from '../services/orderService';
import { menuService } from '../services/menuService';
import { promotionsService } from '../services/promotionService';
import { isPromotionEligible, calculatePromotionDiscount, guestApplicablePromotions } from '../utils/promotions';

const ManualOrderModal = ({ onClose, onOrderCreated, initialOrder }) => {
    const { t } = useTranslation();
    const { effectiveTenantId, tenantData, user, userData } = useAuth();
    const { showAlert } = useAlert();
    const { customers } = useCustomers();

    // Steps: 'table' -> 'menu' -> 'cart' -> 'success'
    const [step, setStep] = useState(initialOrder ? 'cart' : 'table');
    const [tableNumber, setTableNumber] = useState(initialOrder?.tableNumber || '');
    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(false);
    const [menuData, setMenuData] = useState(null);
    const [cart, setCart] = useState(initialOrder?.items || []);
    const [savingOrder, setSavingOrder] = useState(false);
    const [isAppended, setIsAppended] = useState(false);
    const [isEdited, setIsEdited] = useState(false);
    const [discounts, setDiscounts] = useState([]);
    const [appliedPromotion, setAppliedPromotion] = useState(null);

    const getTableDisplayName = (tableId) => {
        if (!tableId || tableId === 'Manuel') return 'Manuel Sipariş';
        if (tableId === 'Paket') return 'Paket Servis / Gel-Al';
        const table = tenantData?.tables?.find(t => String(t.id) === String(tableId));
        if (table) return table.name;
        return String(tableId).startsWith('Masa') ? tableId : `${t('portal.table', 'Masa')} ${tableId}`;
    };

    // Fetch Menu Data
    useEffect(() => {
        const fetchMenu = async () => {
            if (!effectiveTenantId) return;
            setLoading(true);
            try {
                const data = await menuService.fetchMenu(effectiveTenantId);
                if (data) {
                    setMenuData(data);
                }
            } catch (err) {
                console.error("Error fetching menu:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchMenu();
    }, [effectiveTenantId]);

    // Fetch tenant campaigns (guest/manual order applicable ones)
    useEffect(() => {
        if (!effectiveTenantId) return;
        let active = true;
        promotionsService.fetchPromotions(effectiveTenantId).then((data) => {
            if (active) setDiscounts(data || []);
        });
        return () => { active = false; };
    }, [effectiveTenantId]);

    // Prefill applied promotion when editing an order that already had one
    useEffect(() => {
        if (initialOrder?.appliedPromotionId && discounts.length > 0) {
            const found = discounts.find(d => d.id === initialOrder.appliedPromotionId);
            if (found) setAppliedPromotion(prev => prev || found);
        }
    }, [initialOrder, discounts]);

    // Campaigns a waiter can apply to this walk-in/guest order
    const availablePromotions = guestApplicablePromotions(discounts);

    // Drop the applied promotion if the cart no longer satisfies it
    useEffect(() => {
        if (appliedPromotion) {
            const { eligible } = isPromotionEligible(appliedPromotion, cart);
            if (!eligible) setAppliedPromotion(null);
        }
    }, [cart, appliedPromotion]);

    const handleTableSubmit = (e) => {
        e.preventDefault();
        if (!tableNumber) {
            showAlert("Lütfen bir masa numarası giriniz.", "warning");
            return;
        }
        setStep('menu');
    };

    const addToCart = (item) => {
        const prodId = `${item.categoryName}_${item.name}`;
        setCart(prev => {
            const existing = prev.find(i => i.prodId === prodId);
            if (existing) {
                return prev.map(i => i.prodId === prodId ? { ...i, quantity: i.quantity + 1 } : i);
            }
            return [...prev, { ...item, prodId, quantity: 1 }];
        });
    };

    const updateQuantity = (prodId, change) => {
        setCart(prev => {
            return prev.map(item => {
                if (item.prodId === prodId) {
                    const newQty = item.quantity + change;
                    return newQty > 0 ? { ...item, quantity: newQty } : null;
                }
                return item;
            }).filter(Boolean);
        });
    };

    const cartSubtotal = cart.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0);
    const { promoDiscount, promoDetails } = calculatePromotionDiscount(appliedPromotion, cart);
    const cartTotal = Math.max(0, cartSubtotal - promoDiscount);

    // Increment a promotion's usage counter (best-effort, non-blocking failure)
    const incrementPromotionUsage = async (promoId) => {
        try {
            const { data: promoRow } = await supabase
                .from('discounts')
                .select('usage_count')
                .eq('id', promoId)
                .single();
            await supabase
                .from('discounts')
                .update({ usage_count: (promoRow?.usage_count || 0) + 1 })
                .eq('id', promoId);
        } catch (err) {
            console.error('Promotion usage increment failed:', err);
        }
    };

    const handleSaveOrder = async () => {
        if (cart.length === 0) return;
        setSavingOrder(true);

        try {
            if (initialOrder) {
                const orderData = {
                    items: cart,
                    totalAmount: cartTotal,
                    subtotal: cartSubtotal,
                    discountAmount: promoDiscount,
                    appliedPromotionId: appliedPromotion?.id || null,
                    appliedPromotionTitle: appliedPromotion?.title || null,
                    tableNumber: tableNumber,
                };

                await orderService.updateOrderDetails(
                    initialOrder.id,
                    orderData,
                    effectiveTenantId,
                    user.uid,
                    userData?.email
                );

                setIsEdited(true);
                if (onOrderCreated) onOrderCreated(initialOrder.id);
                setStep('success');

                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                // Create Order
                const orderData = {
                    tenantId: effectiveTenantId,
                    customerId: null,
                    customerName: 'Misafir',
                    items: cart,
                    totalAmount: cartTotal,
                    subtotal: cartSubtotal,
                    discountAmount: promoDiscount,
                    appliedPromotionId: appliedPromotion?.id || null,
                    appliedPromotionTitle: appliedPromotion?.title || null,
                    status: 'pending',
                    tableNumber: tableNumber,
                    source: 'waiter_manual',
                    timestamp: new Date().toISOString(),
                    created_at: new Date().toISOString()
                };

                const result = await orderService.createOrder(
                    orderData,
                    effectiveTenantId,
                    user.uid,
                    userData?.email
                );

                // Only count usage for a brand-new order (not when appended to
                // an existing table order, which doesn't store promo metadata).
                if (appliedPromotion && !result.appended) {
                    await incrementPromotionUsage(appliedPromotion.id);
                }

                setIsAppended(result.appended);
                if (onOrderCreated) onOrderCreated(result.id);
                setStep('success');

                setTimeout(() => {
                    onClose();
                }, 2000);
            }

        } catch (err) {
            console.error("Order Action Error:", err);
            showAlert("İşlem sırasında bir hata oluştu.", "error");
        } finally {
            setSavingOrder(false);
        }
    };

    return (
        <div onClick={onClose} style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }}>
            <div onClick={(e) => e.stopPropagation()} className="card glass-premium mobile-modal" style={{
                width: '95vw', maxWidth: '600px', height: '90vh',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                background: 'var(--bg-main)', border: '1px solid rgba(255,255,255,0.1)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
                margin: 'auto'
            }}>
                {/* Header */}
                <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                            <ShoppingBag size={20} />
                        </div>
                        <h2 style={{ fontSize: '1.25rem', fontWeight: 800 }}>{initialOrder ? `Siparişi Düzenle #${initialOrder.id.slice(-6).toUpperCase()}` : 'Yeni Sipariş (Garson)'}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'rgba(0,0,0,0.05)',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '10px',
                            borderRadius: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-main)',
                            transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'}
                    >
                        <X size={20} strokeWidth={2.5} />
                    </button>
                </div>

                {/* Content */}
                <div style={{ flex: 1, overflowY: 'auto', position: 'relative' }}>

                    {/* Step 1: Table Input */}
                    {step === 'table' && (
                        <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem', height: '100%' }}>
                            <div style={{ textAlign: 'center' }}>
                                <h3 style={{ fontSize: '1.5rem', fontWeight: 900, marginBottom: '0.5rem' }}>Masa Seçimi</h3>
                                <p style={{ color: 'var(--text-muted)' }}>Siparişi hangi masaya kaydediyoruz?</p>
                            </div>

                            <div style={{
                                flex: 1,
                                overflowY: 'auto',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                                gap: '12px',
                                padding: '10px 4px',
                                scrollbarWidth: 'thin'
                            }}>
                                {/* Special: Paket Servis */}
                                <button
                                    onClick={() => { setTableNumber('Paket'); setStep('menu'); }}
                                    style={{
                                        padding: '1.25rem 1rem', borderRadius: '20px', border: '1px solid var(--border)',
                                        background: 'rgba(59, 130, 246, 0.05)',
                                        color: 'var(--text-main)',
                                        fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                                >
                                    <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <ShoppingBag size={20} />
                                    </div>
                                    <span style={{ fontSize: '0.9rem' }}>Paket Servis</span>
                                </button>

                                {/* Defined Tables */}
                                {(tenantData?.tables || []).map(t => (
                                    <button
                                        key={t.id}
                                        onClick={() => { setTableNumber(String(t.id)); setStep('menu'); }}
                                        style={{
                                            padding: '1.25rem 1rem', borderRadius: '20px', border: '1px solid var(--border)',
                                            background: 'var(--bg-card)',
                                            color: 'var(--text-main)',
                                            fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s',
                                            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                                        }}
                                        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                                        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                                    >
                                        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Utensils size={20} />
                                        </div>
                                        <span style={{ fontSize: '0.9rem' }}>{t.name}</span>
                                    </button>
                                ))}

                                {/* Special: Manuel Giriş */}
                                <button
                                    onClick={() => { setTableNumber('Manuel'); setStep('menu'); }}
                                    style={{
                                        padding: '1.25rem 1rem', borderRadius: '20px', border: '1px solid var(--border)',
                                        background: 'rgba(16, 185, 129, 0.05)',
                                        color: 'var(--text-main)',
                                        fontWeight: 800, cursor: 'pointer', transition: 'all 0.2s',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.03)'
                                    }}
                                    onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.borderColor = '#10b981'; }}
                                    onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                                >
                                    <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.15)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <User size={20} />
                                    </div>
                                    <span style={{ fontSize: '0.9rem' }}>Manuel Kayıt</span>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Menu Selection */}
                    {step === 'menu' && (
                        loading && !menuData ? (
                            <div style={{ padding: '4rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                                <div className="loading-spinner" style={{ width: '40px', height: '40px', border: '4px solid var(--primary)', borderTopColor: 'transparent' }}></div>
                                <p style={{ color: 'var(--text-muted)', fontWeight: 600 }}>Menü Yükleniyor...</p>
                            </div>
                        ) : menuData ? (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ padding: '0.75rem 1.5rem', background: 'var(--bg-card)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800 }}>
                                    <Utensils size={18} color="var(--primary)" /> {getTableDisplayName(tableNumber)}
                                </div>
                                <button onClick={() => setStep('table')} style={{ background: 'transparent', border: 'none', color: 'var(--primary)', fontSize: '0.8125rem', fontWeight: 700, cursor: 'pointer' }}>Masayı Değiştir</button>
                            </div>

                            <div style={{ flex: 1, overflowY: 'auto', paddingBottom: '100px' }}>
                                <DigitalMenu
                                    menuData={menuData}
                                    onAddToCart={addToCart}
                                    onUpdateQuantity={updateQuantity}
                                    cart={cart}
                                    tenant={tenantData}
                                    tenantPlan={tenantData?.plan || 'pro'}
                                />
                            </div>

                            {/* Cart Bar */}
                            {cart.length > 0 && !savingOrder && (
                                <div style={{
                                    position: 'absolute', bottom: 0, left: 0, right: 0,
                                    padding: '1rem 1.5rem', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)',
                                    borderTop: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '10px',
                                    zIndex: 20, boxShadow: '0 -4px 20px rgba(0,0,0,0.05)'
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)', fontWeight: 700 }}>Toplam Tutar</div>
                                        <div style={{ fontSize: '1.35rem', fontWeight: 900, color: 'var(--primary)' }}>₺{cartTotal.toFixed(2)}</div>
                                    </div>
                                    <button
                                        onClick={() => setStep('cart')}
                                        style={{
                                            width: '100%', padding: '1rem', borderRadius: '16px', border: 'none',
                                            background: 'var(--primary)', color: 'white', fontWeight: 800,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer',
                                            boxShadow: '0 8px 20px rgba(var(--primary-rgb), 0.3)', fontSize: '1.05rem'
                                        }}
                                    >
                                        Siparişi Onayla <ChevronRight size={20} />
                                    </button>
                                </div>
                            )}
                        </div>
                        ) : (
                            <div style={{ padding: '4rem', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', opacity: 0.6 }}>
                                <Utensils size={48} />
                                <p style={{ fontWeight: 600 }}>Menü Bulunamadı</p>
                                <button onClick={() => setStep('table')} className="btn-secondary" style={{ padding: '8px 16px', borderRadius: '10px', border: 'none', background: 'var(--bg-card)', cursor: 'pointer' }}>Geri Dön</button>
                            </div>
                        )
                    )}

                    {/* Step 3: Cart Confirmation */}
                    {step === 'cart' && (
                        <div style={{ padding: '1.5rem', height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <button onClick={() => setStep('menu')} style={{ alignSelf: 'flex-start', background: 'var(--bg-card)', border: '1px solid var(--border)', padding: '8px 16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1.5rem', cursor: 'pointer', fontWeight: 800, color: 'var(--text-main)', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', transition: 'all 0.2s' }} className="hover-bg">
                                <ArrowLeft size={18} /> Menüye Dön
                            </button>

                            <h3 style={{ fontSize: '1.25rem', fontWeight: 900, marginBottom: '1rem' }}>{getTableDisplayName(tableNumber)} - Özet</h3>

                            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {cart.map((item, idx) => (
                                    <div key={idx} style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '1rem',
                                        background: 'var(--bg-card)',
                                        borderRadius: '16px',
                                        border: '1px solid var(--border)',
                                        gap: '10px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                                            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'white', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, color: 'var(--primary)', flexShrink: 0 }}>
                                                {item.quantity}
                                            </div>
                                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                <div style={{ fontWeight: 800, fontSize: '0.875rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</div>
                                                <div style={{ fontSize: '0.75rem', color: 'var(--primary)', fontWeight: 700 }}>₺{item.price}</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'white', padding: '4px 8px', borderRadius: '10px', border: '1px solid var(--border)', flexShrink: 0 }}>
                                            <button
                                                onClick={() => updateQuantity(item.prodId, -1)}
                                                style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', display: 'flex', padding: '2px' }}
                                            >
                                                <Minus size={18} strokeWidth={3} />
                                            </button>
                                            <span style={{ fontWeight: 900, fontSize: '0.875rem', minWidth: '20px', textAlign: 'center' }}>{item.quantity}</span>
                                            <button
                                                onClick={() => updateQuantity(item.prodId, 1)}
                                                style={{ background: 'transparent', border: 'none', color: 'var(--primary)', cursor: 'pointer', display: 'flex', padding: '2px' }}
                                            >
                                                <Plus size={18} strokeWidth={3} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Campaign / Discount Selection */}
                            {availablePromotions.length > 0 && (
                                <div style={{ marginTop: '1.5rem' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.8125rem', fontWeight: 800, marginBottom: '12px', color: 'var(--text-muted)' }}>
                                        <Sparkles size={16} color="var(--primary)" /> Kampanya Seç
                                    </label>
                                    <div style={{ display: 'flex', gap: '12px', overflowX: 'auto', paddingBottom: '8px', scrollbarWidth: 'none' }}>
                                        {availablePromotions
                                            .map(promo => ({ promo, ...isPromotionEligible(promo, cart) }))
                                            .sort((a, b) => (a.eligible ? 0 : 1) - (b.eligible ? 0 : 1))
                                            .map(({ promo, eligible, reason }) => {
                                                const isActive = appliedPromotion?.id === promo.id;
                                                return (
                                                    <div
                                                        key={promo.id}
                                                        onClick={() => {
                                                            if (!eligible && !isActive) return;
                                                            setAppliedPromotion(isActive ? null : promo);
                                                        }}
                                                        style={{
                                                            flexShrink: 0,
                                                            width: '170px',
                                                            padding: '12px',
                                                            borderRadius: '16px',
                                                            background: isActive ? 'rgba(var(--primary-rgb), 0.08)' : 'var(--bg-card)',
                                                            border: `2px solid ${isActive ? 'var(--primary)' : (eligible ? 'rgba(var(--primary-rgb), 0.3)' : 'var(--border)')}`,
                                                            cursor: eligible || isActive ? 'pointer' : 'not-allowed',
                                                            transition: 'all 0.2s',
                                                            position: 'relative',
                                                            opacity: eligible || isActive ? 1 : 0.55
                                                        }}
                                                    >
                                                        {isActive && (
                                                            <div style={{ position: 'absolute', top: '-8px', right: '-8px', background: 'var(--primary)', color: 'white', borderRadius: '50%', width: '22px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <CheckCircle2 size={13} />
                                                            </div>
                                                        )}
                                                        <div style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--primary)', marginBottom: '4px', letterSpacing: '0.5px' }}>
                                                            İNDİRİM
                                                        </div>
                                                        <div style={{ fontSize: '0.8125rem', fontWeight: 700, marginBottom: '6px', lineHeight: 1.2, minHeight: '2.4em', overflow: 'hidden' }}>
                                                            {promo.title}
                                                        </div>
                                                        <div style={{
                                                            fontSize: '0.7rem',
                                                            fontWeight: 800,
                                                            background: isActive ? 'white' : (eligible ? 'var(--primary)' : '#fee2e2'),
                                                            color: isActive ? 'var(--primary)' : (eligible ? 'white' : '#ef4444'),
                                                            padding: '6px 8px',
                                                            borderRadius: '8px',
                                                            textAlign: 'center',
                                                            border: isActive ? '1px solid var(--primary)' : 'none'
                                                        }}>
                                                            {isActive ? 'KULLANILIYOR' : (eligible ? 'UYGULA' : 'UYGUN DEĞİL')}
                                                        </div>
                                                        {!eligible && !isActive && reason && (
                                                            <div style={{ fontSize: '0.65rem', color: '#ef4444', fontWeight: 600, marginTop: '4px', textAlign: 'center' }}>
                                                                {reason}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                </div>
                            )}

                            <div style={{ marginTop: 'auto', paddingTop: '1.5rem', borderTop: '2px dashed var(--border)' }}>
                                {promoDiscount > 0 && (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '1rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-muted)' }}>Ara Toplam</span>
                                            <span style={{ fontSize: '0.9375rem', fontWeight: 700, color: 'var(--text-muted)' }}>₺{cartSubtotal.toFixed(2)}</span>
                                        </div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#22c55e' }}>
                                            <span style={{ fontSize: '0.9375rem', fontWeight: 700 }}>İndirim ({appliedPromotion?.title})</span>
                                            <span style={{ fontSize: '0.9375rem', fontWeight: 800 }}>-₺{promoDiscount.toFixed(2)}</span>
                                        </div>
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                    <span style={{ fontSize: '1.125rem', fontWeight: 800 }}>Toplam Tutar</span>
                                    <span style={{ fontSize: '2rem', fontWeight: 900, color: 'var(--primary)' }}>₺{cartTotal.toFixed(2)}</span>
                                </div>

                                <button
                                    onClick={handleSaveOrder}
                                    disabled={savingOrder}
                                    style={{
                                        width: '100%', padding: '1.25rem', borderRadius: '18px', border: 'none',
                                        background: 'var(--primary)', color: 'white', fontWeight: 800, fontSize: '1.125rem',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px',
                                        cursor: 'pointer', boxShadow: '0 10px 25px rgba(var(--primary-rgb), 0.4)',
                                        opacity: savingOrder ? 0.7 : 1
                                    }}
                                >
                                    {savingOrder ? <div className="loading-spinner" style={{ width: '20px', height: '20px', border: '2px solid white', borderTopColor: 'transparent' }}></div> : <CheckCircle2 size={24} />}
                                    {initialOrder ? 'Değişiklikleri Kaydet' : 'Siparişi Mutfak/Kasaya Gönder'}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'success' && (
                        <div style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '2rem' }}>
                            <div style={{ width: '100px', height: '100px', borderRadius: '50%', background: '#10b981', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '2rem', boxShadow: '0 15px 35px rgba(16, 185, 129, 0.3)' }}>
                                <CheckCircle2 size={50} />
                            </div>
                            <h3 style={{ fontSize: '2rem', fontWeight: 900, color: '#10b981' }}>
                                {isEdited ? 'Sipariş Güncellendi!' : (isAppended ? 'Masaya Eklendi!' : 'Sipariş Alındı!')}
                            </h3>
                            <p style={{ marginTop: '1rem', color: 'var(--text-muted)', fontSize: '1.125rem' }}>
                                {isEdited
                                    ? `${getTableDisplayName(tableNumber)} numaralı sipariş başarıyla güncellendi.`
                                    : (isAppended
                                        ? `${getTableDisplayName(tableNumber)} için mevcut siparişe yeni ürünler başarıyla eklendi.`
                                        : 'Sipariş başarıyla oluşturuldu ve listeye eklendi.')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ManualOrderModal;
